/**
 * Packaging OS — Product Brief + Brand Intake page (Phase 22A).
 *
 * Calls POST /api/workflows/product-brief/dry-run. Renders the
 * deterministic draft response. No AI provider runs in this phase;
 * the engine is rule-based and the page is explicit about it.
 */

import { useState, type FormEvent } from "react";
import {
  ASSETS_AVAILABLE,
  BRAND_MATURITY,
  BUDGET_BANDS,
  DRY_RUN_CHANNELS,
  DRY_RUN_LANGUAGES,
  INTAKE_OBJECTIVES,
  PACKAGING_STATUS,
  PRICE_BANDS,
  PRODUCT_FORMS,
  SIZE_UNITS,
  runProductBrandIntakeDryRun,
  type ApiResult,
  type AssetAvailable,
  type BrandMaturity,
  type BudgetBand,
  type DryRunChannel,
  type DryRunLanguage,
  type IntakeObjective,
  type PackagingStatus,
  type PriceBand,
  type ProductBrandIntakeRequest,
  type ProductBrandIntakeResponse,
  type ProductForm,
  type SizeUnit,
  runWorkflowPersistedFirst,
} from "../api/client";
import { AIAssistToggle } from "../components/AIAssistToggle";
import { Button } from "../components/Button";
import { DataState, RequestIdBadge } from "../components/DataState";
import {
  FieldGrid,
  SelectField,
  TextAreaField,
  TextField,
} from "../components/FormField";
import { BulletList, ReportSection } from "../components/ReportSection";
import { strings, type UiStrings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";

interface FormState {
  product_name: string;
  product_category: string;
  product_subcategory: string;
  product_description: string;
  product_form: ProductForm;
  size_value: string;
  size_unit: string;
  price_band: PriceBand;

  brand_name: string;
  brand_personality: string;
  brand_maturity: BrandMaturity;
  current_packaging_status: PackagingStatus;
  visual_preferences: string;
  tone_of_voice: string;

  target_markets: string; // CSV
  target_languages: string; // space-separated
  sales_channels: string; // space-separated
  target_audience: string;

  objectives: string; // space-separated

  material_constraints: string;
  budget_band: BudgetBand;
  production_constraints: string;
  sustainability_preferences: string; // newline-separated
  claims_to_avoid: string; // newline-separated
  assets_available: string; // space-separated

  language: DryRunLanguage;
  notes: string;
}

const EMPTY: FormState = {
  product_name: "",
  product_category: "",
  product_subcategory: "",
  product_description: "",
  product_form: "other",
  size_value: "",
  size_unit: "",
  price_band: "unknown",

  brand_name: "",
  brand_personality: "",
  brand_maturity: "early_stage",
  current_packaging_status: "none",
  visual_preferences: "",
  tone_of_voice: "",

  target_markets: "",
  target_languages: "",
  sales_channels: "",
  target_audience: "",

  objectives: "new_packaging",

  material_constraints: "",
  budget_band: "unknown",
  production_constraints: "",
  sustainability_preferences: "",
  claims_to_avoid: "",
  assets_available: "",

  language: "en",
  notes: "",
};

type LoadState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "loaded"; result: ApiResult<ProductBrandIntakeResponse> };

interface FieldErrors {
  product_name?: string;
  product_category?: string;
  brand_name?: string;
  objectives?: string;
}

function splitCsv(s: string): string[] {
  return s
    .split(/[,;\n]/)
    .map((x) => x.trim())
    .filter((x) => x.length > 0);
}

function splitSpace(s: string): string[] {
  return s
    .split(/[\s,]+/)
    .map((x) => x.trim())
    .filter((x) => x.length > 0);
}

function splitLines(s: string, max: number): string[] {
  return s
    .split("\n")
    .map((x) => x.trim())
    .filter((x) => x.length > 0)
    .slice(0, max);
}

export function ProductBriefPage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const [form, setForm] = useState<FormState>(() => ({
    ...EMPTY,
    language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
      ? locale
      : "en") as DryRunLanguage,
  }));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [state, setState] = useState<LoadState>({ kind: "idle" });
  const [aiAssist, setAiAssist] = useState<boolean>(false);

  function update<K extends keyof FormState>(k: K, v: FormState[K]): void {
    setForm((p) => ({ ...p, [k]: v }));
  }

  function validate(): FieldErrors {
    const e: FieldErrors = {};
    if (!form.product_name.trim()) e.product_name = t.workflowDryRunRequiredHint;
    if (!form.product_category.trim())
      e.product_category = t.workflowDryRunRequiredHint;
    if (!form.brand_name.trim()) e.brand_name = t.workflowDryRunRequiredHint;
    if (!splitSpace(form.objectives).length)
      e.objectives = t.workflowDryRunRequiredHint;
    return e;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;

    const objectives = splitSpace(form.objectives).filter((x) =>
      (INTAKE_OBJECTIVES as readonly string[]).includes(x),
    ) as IntakeObjective[];
    const targetLanguages = splitSpace(form.target_languages).filter((x) =>
      (DRY_RUN_LANGUAGES as readonly string[]).includes(x),
    ) as DryRunLanguage[];
    const salesChannels = splitSpace(form.sales_channels).filter((x) =>
      (DRY_RUN_CHANNELS as readonly string[]).includes(x),
    ) as DryRunChannel[];
    const assetsAvailable = splitSpace(form.assets_available).filter((x) =>
      (ASSETS_AVAILABLE as readonly string[]).includes(x),
    ) as AssetAvailable[];

    const sizeValueNum = form.size_value.trim()
      ? Number(form.size_value)
      : null;

    const body: ProductBrandIntakeRequest = {
      product_name: form.product_name.trim(),
      product_category: form.product_category.trim(),
      product_subcategory: form.product_subcategory.trim() || null,
      product_description: form.product_description.trim() || null,
      product_form: form.product_form,
      size_value:
        sizeValueNum !== null && Number.isFinite(sizeValueNum)
          ? sizeValueNum
          : null,
      size_unit: (form.size_unit ? (form.size_unit as SizeUnit) : null),
      price_band: form.price_band,
      brand_name: form.brand_name.trim(),
      brand_personality: form.brand_personality.trim() || null,
      brand_maturity: form.brand_maturity,
      current_packaging_status: form.current_packaging_status,
      visual_preferences: form.visual_preferences.trim() || null,
      tone_of_voice: form.tone_of_voice.trim() || null,
      target_markets: splitCsv(form.target_markets)
        .map((m) => m.toUpperCase())
        .slice(0, 8),
      target_languages: targetLanguages,
      sales_channels: salesChannels,
      target_audience: form.target_audience.trim() || null,
      objectives,
      material_constraints: form.material_constraints.trim() || null,
      budget_band: form.budget_band,
      production_constraints: form.production_constraints.trim() || null,
      sustainability_preferences: splitLines(
        form.sustainability_preferences,
        8,
      ),
      claims_to_avoid: splitLines(form.claims_to_avoid, 32),
      assets_available: assetsAvailable,
      language: form.language,
      notes: form.notes.trim() || null,
      ai_assist: aiAssist,
    };

    setState({ kind: "submitting" });
    const result = await runWorkflowPersistedFirst("product-brief", body, runProductBrandIntakeDryRun);
    setState({ kind: "loaded", result });
  }

  function onReset(): void {
    setForm({
      ...EMPTY,
      language: (DRY_RUN_LANGUAGES.includes(locale as DryRunLanguage)
        ? locale
        : "en") as DryRunLanguage,
    });
    setErrors({});
    setState({ kind: "idle" });
  }

  const opt = (vals: readonly string[]) =>
    vals.map((v) => ({ value: v, label: v }));

  return (
    <AppLayout>
      <header className="po-section-header">
        <span className="po-section-header__eyebrow">
          {t.appShellNavWorkflows}
        </span>
        <h1 className="po-section-header__title">{t.intakeTitle}</h1>
        <p className="po-note">{t.intakeSubtitle}</p>
      </header>

      <section className="po-card">
        <form className="po-form" onSubmit={onSubmit} noValidate>
          <h2 className="po-card__title">{t.intakeGroupProduct}</h2>
          <FieldGrid>
            <TextField
              label={t.intakeFieldProductName}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.product_name}
              onChange={(v) => update("product_name", v)}
              error={errors.product_name}
              maxLength={200}
            />
            <TextField
              label={t.intakeFieldProductCategory}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.product_category}
              onChange={(v) => update("product_category", v)}
              error={errors.product_category}
              maxLength={100}
            />
            <TextField
              label={t.intakeFieldProductSubcategory}
              value={form.product_subcategory}
              onChange={(v) => update("product_subcategory", v)}
              maxLength={100}
            />
            <SelectField
              label={t.intakeFieldProductForm}
              value={form.product_form}
              onChange={(v) => update("product_form", v as ProductForm)}
              options={opt(PRODUCT_FORMS)}
            />
            <TextField
              label={t.intakeFieldSizeValue}
              type="text"
              value={form.size_value}
              onChange={(v) => update("size_value", v)}
              maxLength={20}
            />
            <SelectField
              label={t.intakeFieldSizeUnit}
              value={form.size_unit}
              onChange={(v) => update("size_unit", v)}
              options={opt(SIZE_UNITS)}
              emptyLabel={t.workflowDryRunNotSelected}
            />
            <SelectField
              label={t.intakeFieldPriceBand}
              value={form.price_band}
              onChange={(v) => update("price_band", v as PriceBand)}
              options={opt(PRICE_BANDS)}
            />
            <TextAreaField
              label={t.intakeFieldProductDescription}
              value={form.product_description}
              onChange={(v) => update("product_description", v)}
              rows={3}
              maxLength={2000}
              full
            />
          </FieldGrid>

          <h2 className="po-card__title">{t.intakeGroupBrand}</h2>
          <FieldGrid>
            <TextField
              label={t.intakeFieldBrandName}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.brand_name}
              onChange={(v) => update("brand_name", v)}
              error={errors.brand_name}
              maxLength={200}
            />
            <SelectField
              label={t.intakeFieldBrandMaturity}
              value={form.brand_maturity}
              onChange={(v) => update("brand_maturity", v as BrandMaturity)}
              options={opt(BRAND_MATURITY)}
            />
            <SelectField
              label={t.intakeFieldCurrentPackagingStatus}
              value={form.current_packaging_status}
              onChange={(v) =>
                update("current_packaging_status", v as PackagingStatus)
              }
              options={opt(PACKAGING_STATUS)}
            />
            <TextField
              label={t.intakeFieldToneOfVoice}
              value={form.tone_of_voice}
              onChange={(v) => update("tone_of_voice", v)}
              maxLength={500}
            />
            <TextAreaField
              label={t.intakeFieldBrandPersonality}
              value={form.brand_personality}
              onChange={(v) => update("brand_personality", v)}
              rows={2}
              maxLength={500}
              full
            />
            <TextAreaField
              label={t.intakeFieldVisualPreferences}
              value={form.visual_preferences}
              onChange={(v) => update("visual_preferences", v)}
              rows={2}
              maxLength={500}
              full
            />
          </FieldGrid>

          <h2 className="po-card__title">{t.intakeGroupMarket}</h2>
          <FieldGrid>
            <TextField
              label={t.intakeFieldTargetMarkets}
              hint={t.intakeFieldTargetMarketsHelp}
              value={form.target_markets}
              onChange={(v) => update("target_markets", v)}
              maxLength={120}
            />
            <TextField
              label={t.intakeFieldTargetLanguages}
              hint="en ar fa tr"
              value={form.target_languages}
              onChange={(v) => update("target_languages", v)}
              maxLength={60}
            />
            <TextField
              label={t.intakeFieldSalesChannels}
              hint={DRY_RUN_CHANNELS.join(" ")}
              value={form.sales_channels}
              onChange={(v) => update("sales_channels", v)}
              maxLength={120}
            />
            <TextField
              label={t.intakeFieldTargetAudience}
              value={form.target_audience}
              onChange={(v) => update("target_audience", v)}
              maxLength={500}
            />
          </FieldGrid>

          <h2 className="po-card__title">{t.intakeGroupObjective}</h2>
          <FieldGrid>
            <TextField
              label={t.intakeFieldObjectives}
              hint={`${t.intakeFieldObjectivesHelp} (${INTAKE_OBJECTIVES.join(" ")})`}
              required
              requiredLabel={t.workflowDryRunRequiredHint}
              value={form.objectives}
              onChange={(v) => update("objectives", v)}
              error={errors.objectives}
              maxLength={300}
              full
            />
          </FieldGrid>

          <h2 className="po-card__title">{t.intakeGroupConstraints}</h2>
          <FieldGrid>
            <SelectField
              label={t.intakeFieldBudgetBand}
              value={form.budget_band}
              onChange={(v) => update("budget_band", v as BudgetBand)}
              options={opt(BUDGET_BANDS)}
            />
            <TextField
              label={t.intakeFieldMaterialConstraints}
              value={form.material_constraints}
              onChange={(v) => update("material_constraints", v)}
              maxLength={500}
            />
            <TextField
              label={t.intakeFieldProductionConstraints}
              value={form.production_constraints}
              onChange={(v) => update("production_constraints", v)}
              maxLength={500}
            />
            <SelectField
              label={t.intakeFieldLanguage}
              value={form.language}
              onChange={(v) => update("language", v as DryRunLanguage)}
              options={opt(DRY_RUN_LANGUAGES)}
            />
            <TextAreaField
              label={t.intakeFieldSustainabilityPreferences}
              hint={t.intakeFieldSustainabilityPreferencesHelp}
              value={form.sustainability_preferences}
              onChange={(v) => update("sustainability_preferences", v)}
              rows={3}
              full
            />
            <TextAreaField
              label={t.intakeFieldClaimsToAvoid}
              hint={t.intakeFieldClaimsToAvoidHelp}
              value={form.claims_to_avoid}
              onChange={(v) => update("claims_to_avoid", v)}
              rows={3}
              full
            />
          </FieldGrid>

          <h2 className="po-card__title">{t.intakeGroupAssets}</h2>
          <FieldGrid>
            <TextField
              label={t.intakeFieldAssetsAvailable}
              hint={ASSETS_AVAILABLE.join(" ")}
              value={form.assets_available}
              onChange={(v) => update("assets_available", v)}
              maxLength={200}
              full
            />
            <TextAreaField
              label={t.intakeFieldNotes}
              value={form.notes}
              onChange={(v) => update("notes", v)}
              rows={2}
              maxLength={1000}
              full
            />
          </FieldGrid>

          {Object.keys(errors).length > 0 && (
            <p className="po-field__error">
              {t.workflowDryRunValidationFailed}
            </p>
          )}

          <AIAssistToggle
            checked={aiAssist}
            onChange={setAiAssist}
            label={t.aiAssistToggleLabel}
            hint={t.aiAssistToggleHint}
            banner={t.aiAssistDraftBanner}
            disabled={state.kind === "submitting"}
          />

          <div className="po-row">
            <Button
              variant="primary"
              type="submit"
              disabled={state.kind === "submitting"}
            >
              {state.kind === "submitting"
                ? t.intakeSubmitting
                : t.intakeSubmit}
            </Button>
            <Button
              variant="secondary"
              type="button"
              onClick={onReset}
              disabled={state.kind === "submitting"}
            >
              {t.intakeReset}
            </Button>
          </div>
        </form>
      </section>

      {state.kind === "loaded" && (
        <DataState
          state={{ kind: "loaded", result: state.result }}
          t={t}
          renderOk={(data, requestId) => (
            <IntakeReport data={data} requestId={requestId} t={t} />
          )}
        />
      )}
    </AppLayout>
  );
}

interface IntakeReportProps {
  data: ProductBrandIntakeResponse;
  requestId: string | null;
  t: UiStrings;
}

function NextEngineLink({ t }: { t: UiStrings }): JSX.Element {
  const [locale] = useLocale();
  const target = `/${locale}/app/workflows/label-copy`;
  return (
    <p className="po-row">
      <a
        href={target}
        className="po-btn po-btn--ghost"
        data-testid="po-intake-next-label-copy"
      >
        {t.intakeNextLabelCopy} →
      </a>
    </p>
  );
}

function NextMaterialLink({ t }: { t: UiStrings }): JSX.Element {
  const [locale] = useLocale();
  const target = `/${locale}/app/workflows/material-structure`;
  return (
    <p className="po-row">
      <a
        href={target}
        className="po-btn po-btn--ghost"
        data-testid="po-intake-next-material"
      >
        {t.intakeNextMaterial} →
      </a>
    </p>
  );
}

function NextVisualLink({ t }: { t: UiStrings }): JSX.Element {
  const [locale] = useLocale();
  const target = `/${locale}/app/workflows/visual-direction`;
  return (
    <p className="po-row">
      <a
        href={target}
        className="po-btn po-btn--ghost"
        data-testid="po-intake-next-visual"
      >
        {t.intakeNextVisual} →
      </a>
    </p>
  );
}

function IntakeReport({
  data,
  requestId,
  t,
}: IntakeReportProps): JSX.Element {
  return (
    <div className="po-stack" data-testid="po-intake-report">
      <ReportSection title={t.intakeReportProductBrief}>
        <BulletList items={data.product_brief_summary} />
      </ReportSection>
      <ReportSection title={t.intakeReportBrandContext}>
        <BulletList items={data.brand_context_summary} />
      </ReportSection>
      <ReportSection title={t.intakeReportObjectiveSummary}>
        <BulletList items={data.packaging_objective_summary} />
      </ReportSection>
      <ReportSection title={t.intakeReportMarketChannelFit}>
        <BulletList items={data.market_channel_fit_notes} />
      </ReportSection>
      <ReportSection title={t.intakeReportMissingChecklist}>
        <BulletList
          items={data.missing_information_checklist}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.intakeReportAssumptionLog}>
        <BulletList items={data.assumption_log} empty={t.reportNoFlags} />
      </ReportSection>
      <ReportSection title={t.intakeReportInitialRiskFlags}>
        <BulletList items={data.initial_risk_flags} empty={t.reportNoFlags} />
      </ReportSection>
      <ReportSection title={t.intakeReportHumanReview}>
        <BulletList
          items={data.human_review_required_notes}
          empty={t.reportNoFlags}
        />
      </ReportSection>
      <ReportSection title={t.intakeReportRecommendedNextEngines}>
        <BulletList items={data.recommended_next_engines} />
        {data.recommended_next_engines.includes("label-copy") && (
          <NextEngineLink t={t} />
        )}
        {data.recommended_next_engines.includes("material-structure") && (
          <NextMaterialLink t={t} />
        )}
        {data.recommended_next_engines.includes("visual-direction") && (
          <NextVisualLink t={t} />
        )}
      </ReportSection>
      {data.packaging_strategy_snapshot && (
        <ReportSection title={t.intakeReportStrategy}>
          <BulletList items={data.packaging_strategy_snapshot} />
        </ReportSection>
      )}
      {data.target_customer_interpretation && (
        <ReportSection title={t.intakeReportTargetCustomer}>
          <BulletList items={data.target_customer_interpretation} />
        </ReportSection>
      )}
      {data.positioning_notes && (
        <ReportSection title={t.intakeReportPositioning}>
          <BulletList items={data.positioning_notes} />
        </ReportSection>
      )}
      {data.channel_readiness_notes && (
        <ReportSection title={t.intakeReportChannelReadiness}>
          <BulletList items={data.channel_readiness_notes} />
        </ReportSection>
      )}
      {data.language_localization_notes && (
        <ReportSection title={t.intakeReportLocalization}>
          <BulletList items={data.language_localization_notes} />
        </ReportSection>
      )}
      {data.packaging_decision_priorities && (
        <ReportSection title={t.intakeReportDecisionPriorities}>
          <BulletList items={data.packaging_decision_priorities} />
        </ReportSection>
      )}
      {data.owner_questions_to_resolve && (
        <ReportSection title={t.intakeReportOwnerQuestions}>
          <BulletList items={data.owner_questions_to_resolve} />
        </ReportSection>
      )}
      <ReportSection title={t.reportTrustNotes}>
        <dl className="po-dl">
          <dt>{t.trustNotesSourceBacked}</dt>
          <dd>
            {data.trust_notes.source_backed
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
          <dt>{t.trustNotesRegulatoryGuarantee}</dt>
          <dd>
            {data.trust_notes.regulatory_guarantee
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
          <dt>{t.trustNotesFinalOutput}</dt>
          <dd>
            {data.trust_notes.final_output
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
          <dt>{t.reportClaimRiskReviewRequired}</dt>
          <dd>
            {data.trust_notes.human_review_required
              ? t.reportReviewRequiredYes
              : t.reportReviewRequiredNo}
          </dd>
        </dl>
      </ReportSection>
      <ReportSection title={t.reportNextSteps}>
        <BulletList items={data.next_steps} />
      </ReportSection>
      {requestId && <RequestIdBadge t={t} requestId={requestId} />}
    </div>
  );
}
